import "./App.css"
import Routes from "./routing/routing"
import { LoginBoundary } from "@curefit/pandora"
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Provider } from "react-redux"
import { store } from "./reducers/rootReducer"
import React, { createContext, FC, Suspense, useContext, useEffect, useState } from "react"
import { UserPermissionProvider } from "@curefit/commons-rc-rbac"
// import eventTracker from "./utils/eventTracker"
import { ConfigProvider, theme } from "antd"
import { AppVersionKey, Themes } from "./constants"
import * as LocalStorageUtils from "./utils/localStorage"
import { getItems, remove, setItem } from "./utils/localStorage"
import { darkThemedComponent, lightThemedComponent, themeMapper } from "./styles/themes"
import { HMSRoomProvider } from "@100mslive/react-sdk"
import { datadogRum } from "@datadog/browser-rum"
import ErrorBoundary from "./modules/ErrorBoundary/ErrorBoundary"
import { ApiManager } from "./api/ApiManager"
import { get, isNil } from "lodash"
import useConstructor from "./hooks/useConstructor"
import { useSession } from "./hooks/useSession"
import { APP_CONFIG_KEYS_ENUM } from "./api/appConfig/IAppConfigServiceApis"
import { useAllSubscriptionPacksQuery } from "./hooks/commonQueries"
import { datadogLogs } from "@datadog/browser-logs"
import { useNetworkStatsLogger } from "./utils/useNetworkStats"
import { v4 as uuidv4 } from "uuid"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { toast } from "./utils/toast"
import { ErrorLogger } from "./ErrorLogger"
import Cookies from "universal-cookie"

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error, query: any) => {
			if (query?.meta?.errorMsg) {
				toast.error("Something went wrong", query.meta.errorMsg)
				console.error("Error in query: ", query, error)
			}
		},
	}),
})
// eventTracker.initialize()

const eventsChannel = new BroadcastChannel("user-events")

// if ("serviceWorker" in navigator) {
// 	window.addEventListener("load", () => {
// 		navigator.serviceWorker.register("/service-worker.ts", { scope: "/" })
// 	})
// }

if (import.meta.env.PROD && !location?.origin?.includes("stage")) {
	datadogRum.init({
		applicationId: "6305aa38-7f6b-4caa-9f48-06957c73d8c0",
		clientToken: "pub41d1ee70a65521a8f7f74403a785d395",
		site: "datadoghq.com",
		service: "synapse",
		env: "prod",
		version: `1.${getItems(AppVersionKey) || 0}`,
		sessionSampleRate: 100,
		sessionReplaySampleRate: 20,
		trackUserInteractions: true,
		trackResources: true,
		trackLongTasks: true,
		defaultPrivacyLevel: "mask-user-input",
	})
	datadogRum.startSessionReplayRecording()

	datadogLogs.init({
		clientToken: "pub41d1ee70a65521a8f7f74403a785d395",
		site: "datadoghq.com",
		forwardErrorsToLogs: false,
		sessionSampleRate: 100,
		forwardConsoleLogs: [],
		forwardReports: [],
	})
}

export const DarkThemeContext = createContext({ isDarkMode: false, setDarkMode: null as any })

const CommonReactQueries: FC = () => {
	useAllSubscriptionPacksQuery()
	return <React.Fragment />
}

const cookies = new Cookies()

function App() {
	const [themeValue, setThemeValue] = useState<Themes>(LocalStorageUtils.getThemeValue() || (matchMedia("(prefers-color-scheme: dark)").matches ? Themes.DARK : Themes.LIGHT))
	const { defaultAlgorithm, darkAlgorithm } = theme

	// window.addEventListener("vite:preloadError", (event) => {
	// 	datadogLogs?.logger?.info?.("Dynamic import error occurred, Refreshing the page!")
	// 	location.reload()
	// })
	const getAppVersion = async () => {
		const resp = await ApiManager.getInternalApis().getAppVersion()
		const currentAppVersion = getItems(AppVersionKey)
		if (isNil(currentAppVersion) && !isNil(resp?.version)) {
			setItem(AppVersionKey, resp?.version)
		} else if (!isNil(resp?.version) && currentAppVersion != resp?.version) {
			setItem(AppVersionKey, resp?.version)
			document.getElementById("new-version-popup").style.display = "block"
			console.log(`\n\n**************\n New Version is available\n Current Version: ${currentAppVersion}\nNew Version: ${resp?.version} \n\n *******************\n\n`)
		}
	}

	useConstructor(() => {
		getAppVersion()
	})
	useSession()

	useEffect(() => {
		remove([AppVersionKey])
		document.getElementById("splash-screen")?.classList?.add?.("slide-up")
		setInterval(() => document.getElementById("splash-screen")?.remove?.(), 2000)
	}, [])

	const getHeaders = () => {
		const userEmail = get(cookies.get("userContext"), "email", "")
		const identityId = get(cookies.get("userContext"), "id", "")
		const erpToken = cookies.get("erpToken")
		return {
			"x-synapse-request-id": uuidv4(),
			"x-user-email": userEmail,
			"x-identity-id": identityId,
			"x-erp-token": erpToken,
		}
	}

	return (
		<HMSRoomProvider isHMSStatsOn={true}>
			<DarkThemeContext.Provider value={{ isDarkMode: themeValue === Themes.DARK, setDarkMode: setThemeValue }}>
				<ConfigProvider
					theme={{
						token: themeMapper[themeValue],
						algorithm: themeValue === Themes.DARK ? darkAlgorithm : defaultAlgorithm,
						components: themeValue === Themes.DARK ? darkThemedComponent : lightThemedComponent,
					}}
				>
					<ErrorBoundary>
						{/*@ts-ignore*/}

						<UserPermissionProvider getHeaders={getHeaders}>
							<QueryClientProvider client={queryClient}>
								<LoginBoundary>
									<Provider store={store}>
										<Suspense>
											<Routes />
										</Suspense>
										<CommonReactQueries />
										<ReactQueryDevtools />
									</Provider>
								</LoginBoundary>
							</QueryClientProvider>
						</UserPermissionProvider>
					</ErrorBoundary>
				</ConfigProvider>
			</DarkThemeContext.Provider>
		</HMSRoomProvider>
	)
}

export default App
